import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Patient } from '@/entities/patient/types'
import { createId } from '@/shared/lib/id'
import { includesNormalized, normalizeIdentity } from '@/shared/lib/normalize'
import { getErrorMessage, isLikelyOfflineError, retryOnNetworkOrAuthError } from '@/shared/lib/network'
import { calculateAgeFromBirthDate, normalizePatientSex } from '@/shared/lib/patient'
import { indexedDBJSON } from '@/shared/lib/persistence'
import { AppError } from '@/shared/lib/appError'
import { logClientError } from '@/shared/lib/safeClientLog'
import { useOutboxStore } from '@/features/offline/model/outboxStore'
import { useSessionStore } from '@/features/session/model/sessionStore'
import {
  patientsRepository,
  type PatientLinkRole,
  type PatientRelationship,
  type RepoPatient,
} from '@/shared/repositories/patients/patientsRepository'

export interface PatientUpsertPayload {
  name: string
  tags?: string[]
  documentNumber?: string
  email?: string
  /** PAC-06: texto digitado ou E.164; vazio apaga. */
  phone?: string | null
  city?: string
  isGestatingOrLactating?: boolean | null
  sex?: string
  birthDate?: string
  age?: number
  relationship?: PatientRelationship
  linkRole?: PatientLinkRole
  summary?: string
  comment?: string
  attendanceTypeId?: string | null
  lastWeightKg?: number | null
}

interface PatientStoreState {
  patients: Patient[]
  loaded: boolean
  fetchPatients: () => Promise<void>
  addOrUpdatePatient: (name: string) => Patient | null
  upsertPatient: (payload: PatientUpsertPayload) => Patient | null
  upsertPatientAsync: (payload: PatientUpsertPayload) => Promise<Patient | null>
  updatePatientAsync: (patientId: string, payload: PatientUpsertPayload) => Promise<Patient | null>
  deactivatePatientForCurrentUser: (patientId: string) => Promise<void>
  updatePatientComment: (patientId: string, comment: string) => void
  importPatients: (
    payloads: PatientUpsertPayload[],
  ) => { created: number; updated: number; skipped: number }
  getPatientById: (patientId: string) => Patient | undefined
  searchPatients: (query: string) => Patient[]
  getRecentPatients: (limit?: number) => Patient[]
}

const sortByCreatedAtDesc = (patients: Patient[]): Patient[] =>
  [...patients].sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  )

/** Ordena por última atividade (updatedAt ou createdAt) para exibir pacientes recentes. */
const sortByLastActivityDesc = (patients: Patient[]): Patient[] =>
  [...patients].sort(
    (left, right) =>
      new Date(right.updatedAt ?? right.createdAt).getTime() -
      new Date(left.updatedAt ?? left.createdAt).getTime(),
  )

const repoToPatient = (p: RepoPatient): Patient => ({
  id: p.id,
  name: p.name,
  normalizedName: p.normalizedName,
  tags: p.tags,
  documentNumber: p.documentNumber,
  email: p.email,
  phone: p.phone,
  sex: normalizePatientSex(p.sex),
  birthDate: p.birthDate,
  age: p.age,
  city: p.city,
  summary: p.summary,
  comment: p.comment,
  attendanceTypeId: p.attendanceTypeId,
  attendanceTypeName: p.attendanceTypeName,
  isGestatingOrLactating: p.isGestatingOrLactating,
  lastWeightKg: p.lastWeightKg,
  lastWeightAt: p.lastWeightAt,
  createdAt: p.createdAt,
  updatedAt: p.updatedAt,
})

const buildPatientData = (payload: PatientUpsertPayload) => {
  const trimmedName = payload.name.trim()
  const documentNumber = payload.documentNumber?.trim() || undefined
  const email = payload.email?.trim() || undefined
  const city = payload.city?.trim() || undefined
  const sex = normalizePatientSex(payload.sex)
  const normalizedBirthDate = payload.birthDate?.trim() || undefined
  const ageFromBirthDate = normalizedBirthDate
    ? calculateAgeFromBirthDate(normalizedBirthDate)
    : null
  const age = ageFromBirthDate !== null ? ageFromBirthDate : undefined
  const summary = payload.summary?.trim() || undefined
  const comment = payload.comment?.trim() || undefined
  const tags = payload.tags
    ? [...new Set(payload.tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0))]
    : undefined

  return {
    trimmedName,
    normalizedName: normalizeIdentity(trimmedName),
    documentNumber,
    email,
    city,
    sex,
    birthDate: normalizedBirthDate,
    age,
    tags,
    summary,
    comment,
    attendanceTypeId: payload.attendanceTypeId,
    isGestatingOrLactating: payload.isGestatingOrLactating,
    lastWeightKg: payload.lastWeightKg ?? undefined,
  }
}

export const usePatientStore = create<PatientStoreState>()(
  persist(
    (set, get) => ({
      patients: [],
      loaded: false,

      fetchPatients: async () => {
        try {
          const { patients } = await patientsRepository.list()
          set({ patients: sortByCreatedAtDesc(patients.map(repoToPatient)), loaded: true })
        } catch (error) {
          if (error instanceof AppError && error.code === 'UNAUTHORIZED') {
            logClientError('[login] fetchPatients: sessão inválida (401)', error)
            useSessionStore.getState().logout()
            throw error
          }
          logClientError('[login] fetchPatients: falha ao carregar', error)
          /* keep local state on failure */
        }
      },

      addOrUpdatePatient: (name) => {
        return get().upsertPatient({ name })
      },

      upsertPatient: (payload) => {
        const {
          trimmedName,
          normalizedName,
          documentNumber,
          email,
          city,
          sex,
          birthDate,
          age,
          tags,
          summary,
          comment,
        } =
          buildPatientData(payload)

        if (!trimmedName) return null

        let nextPatient: Patient | null = null

        set((state) => {
          const existingPatient = state.patients.find(
            (patient) => patient.normalizedName === normalizedName,
          )

          if (existingPatient) {
            const now = new Date().toISOString()
            nextPatient = {
              ...existingPatient,
              name: trimmedName,
              documentNumber: documentNumber ?? existingPatient.documentNumber,
              email: email ?? existingPatient.email,
              tags: tags ?? existingPatient.tags,
              city,
              sex: sex ?? existingPatient.sex,
              birthDate: birthDate ?? existingPatient.birthDate,
              age: age ?? existingPatient.age,
              summary: summary ?? existingPatient.summary,
              comment: comment ?? existingPatient.comment,
              isGestatingOrLactating: payload.isGestatingOrLactating ?? existingPatient.isGestatingOrLactating,
              lastWeightKg:
                payload.lastWeightKg !== undefined
                  ? payload.lastWeightKg ?? undefined
                  : existingPatient.lastWeightKg,
              lastWeightAt:
                payload.lastWeightKg !== undefined && payload.lastWeightKg != null
                  ? now
                  : payload.lastWeightKg === null
                    ? undefined
                    : existingPatient.lastWeightAt,
              updatedAt: now,
            }
            return {
              patients: state.patients.map((patient) =>
                patient.id === existingPatient.id ? nextPatient! : patient,
              ),
            }
          }

          const now = new Date().toISOString()
          nextPatient = {
            id: createId(),
            name: trimmedName,
            normalizedName,
            documentNumber,
            email,
            tags,
            createdAt: now,
            updatedAt: now,
            city, sex, birthDate, age, summary, comment,
            isGestatingOrLactating: payload.isGestatingOrLactating,
            lastWeightKg: payload.lastWeightKg ?? undefined,
            lastWeightAt: payload.lastWeightKg != null ? now : undefined,
          }
          return { patients: sortByCreatedAtDesc([nextPatient, ...state.patients]) }
        })

        if (nextPatient) {
          const repoPayload = {
            name: trimmedName,
            documentNumber,
            email,
            sex,
            birthDate,
            city,
            tags,
            summary,
            comment,
            isGestatingOrLactating: payload.isGestatingOrLactating,
            lastWeightKg: payload.lastWeightKg,
            relationship: payload.relationship,
            linkRole: payload.linkRole,
          }

          const sessionUser = useSessionStore.getState().user
          const actor =
            sessionUser?.id && sessionUser.role
              ? {
                  id: sessionUser.id,
                  role: sessionUser.role,
                  isSystemAdmin: Boolean(sessionUser.isSystemAdmin),
                }
              : undefined

          patientsRepository
            .upsert(repoPayload, { actor })
            .then(({ patient }) => {
              const local = get().patients.find((p) => p.normalizedName === normalizedName)
              if (local && local.id !== patient.id) {
                set((state) => ({
                  patients: state.patients.map((p) =>
                    p.id === local.id ? { ...p, id: patient.id } : p,
                  ),
                }))
              }
            })
            .catch((error) => {
              if (!isLikelyOfflineError(error)) return
              useOutboxStore.getState().enqueue(
                'patient.upsert',
                repoPayload,
                getErrorMessage(error, 'Falha de conexão ao sincronizar paciente.'),
              )
            })
        }

        return nextPatient
      },

      upsertPatientAsync: async (payload) => {
        const { trimmedName } = buildPatientData(payload)
        if (!trimmedName) return null

        const sessionUser = useSessionStore.getState().user
        const actor =
          sessionUser?.id && sessionUser.role
            ? {
                id: sessionUser.id,
                role: sessionUser.role,
                isSystemAdmin: Boolean(sessionUser.isSystemAdmin),
              }
            : undefined

        try {
          const { patient: repoPatient } = await retryOnNetworkOrAuthError(() =>
            patientsRepository.upsert(payload, { actor }),
          )
          const p = repoToPatient(repoPatient)
          set((state) => {
            const exists = state.patients.find((x) => x.id === p.id)
            if (exists) {
              return { patients: state.patients.map((x) => (x.id === p.id ? p : x)) }
            }
            return { patients: sortByCreatedAtDesc([p, ...state.patients]) }
          })
          return p
        } catch (error) {
          if (error instanceof AppError && error.code === 'UNAUTHORIZED') {
            useSessionStore.getState().logout()
            throw error
          }
          if (isLikelyOfflineError(error)) {
            const optimistic = get().upsertPatient(payload)
            if (optimistic) return optimistic
          }
          if (error instanceof AppError) {
            throw error
          }
          return get().upsertPatient(payload)
        }
      },

      updatePatientAsync: async (patientId, payload) => {
        const existing = get().patients.find((patient) => patient.id === patientId)
        if (!existing) return null

        const mergedPayload: PatientUpsertPayload = {
          ...existing,
          ...payload,
          name: payload.name ?? existing.name,
        }

        const {
          trimmedName,
          normalizedName,
          documentNumber,
          email,
          city,
          sex,
          birthDate,
          age,
          tags,
          summary,
          comment,
          attendanceTypeId,
          isGestatingOrLactating,
          lastWeightKg,
        } = buildPatientData(mergedPayload)

        if (!trimmedName) return null

        const weightNow = new Date().toISOString()
        const optimisticPatient: Patient = {
          ...existing,
          name: trimmedName,
          normalizedName,
          documentNumber,
          email,
          tags,
          city,
          sex,
          birthDate,
          age,
          summary,
          comment,
          attendanceTypeId: attendanceTypeId ?? existing.attendanceTypeId,
          attendanceTypeName: existing.attendanceTypeName,
          isGestatingOrLactating: isGestatingOrLactating ?? existing.isGestatingOrLactating,
          lastWeightKg:
            payload.lastWeightKg !== undefined ? lastWeightKg ?? undefined : existing.lastWeightKg,
          lastWeightAt:
            payload.lastWeightKg !== undefined && lastWeightKg != null
              ? weightNow
              : payload.lastWeightKg === null
                ? undefined
                : existing.lastWeightAt,
          updatedAt: weightNow,
        }

        set((state) => ({
          patients: state.patients.map((patient) =>
            patient.id === patientId ? optimisticPatient : patient,
          ),
        }))

        try {
          const { patient: repoPatient } = await patientsRepository.update(patientId, {
            name: trimmedName,
            documentNumber,
            email,
            ...(payload.phone !== undefined ? { phone: payload.phone } : {}),
            city,
            sex,
            birthDate,
            tags,
            summary,
            comment,
            attendanceTypeId: attendanceTypeId ?? null,
            isGestatingOrLactating: isGestatingOrLactating ?? null,
            lastWeightKg: payload.lastWeightKg !== undefined ? lastWeightKg : undefined,
          })
          const updated = repoToPatient(repoPatient)
          set((state) => ({
            patients: state.patients.map((patient) =>
              patient.id === patientId ? updated : patient,
            ),
          }))
          return updated
        } catch (error) {
          if (error instanceof AppError && error.code === 'UNAUTHORIZED') {
            useSessionStore.getState().logout()
          }
          set((state) => ({
            patients: state.patients.map((patient) =>
              patient.id === patientId ? existing : patient,
            ),
          }))
          return null
        }
      },

      deactivatePatientForCurrentUser: async (patientId) => {
        const previousPatients = get().patients
        set((state) => ({
          patients: state.patients.filter((patient) => patient.id !== patientId),
        }))

        try {
          await patientsRepository.deactivateForCurrentUser(patientId)
        } catch {
          set({ patients: previousPatients })
        }
      },

      updatePatientComment: (patientId, comment) => {
        const nextComment = comment.trim() || undefined
        set((state) => ({
          patients: state.patients.map((patient) =>
            patient.id === patientId ? { ...patient, comment: nextComment } : patient,
          ),
        }))
      },

      importPatients: (payloads) => {
        let created = 0
        let updated = 0
        let skipped = 0

        payloads.forEach((payload) => {
          if (!payload.name?.trim()) { skipped += 1; return }
          const normalizedName = normalizeIdentity(payload.name)
          const existingPatient = get().patients.find(
            (patient) => patient.normalizedName === normalizedName,
          )
          const savedPatient = get().upsertPatient(payload)
          if (!savedPatient) { skipped += 1; return }
          if (existingPatient) { updated += 1 } else { created += 1 }
        })

        return { created, updated, skipped }
      },

      getPatientById: (patientId) =>
        get().patients.find((patient) => patient.id === patientId),

      searchPatients: (query) =>
        sortByCreatedAtDesc(
          get().patients.filter((patient) => includesNormalized(patient.name, query)),
        ),

      getRecentPatients: (limit = 3) => sortByLastActivityDesc(get().patients).slice(0, limit),
    }),
    {
      name: 'synap-patient-store',
      storage: indexedDBJSON,
      partialize: (state) => ({ patients: state.patients }),
      skipHydration: true,
    },
  ),
)
